// client/src/components/agent/actions/InteractiveBuilder.jsx
// Formulario controlado para armar un mensaje interactivo de WhatsApp:
// encabezado (media opcional), cuerpo, pie opcional y una interacción que puede
// ser botones de respuesta rápida, un botón de enlace (URL) o una lista.
// Los inputs limitan la escritura a lo que permite Meta (ver interactiveLimits.js).
import { useState } from 'react';
import theme from '../../../theme';
import { LIMITS } from './interactiveLimits';

const HEADER_TYPES = [
  { v: 'image', label: 'Imagen' },
  { v: 'video', label: 'Video' },
  { v: 'document', label: 'Documento' },
];

const ACTION_TYPES = [
  { v: 'quick_reply', label: 'Botones de respuesta rápida' },
  { v: 'url', label: 'Botón de enlace (URL)' },
  { v: 'list', label: 'Lista de opciones' },
];

export default function InteractiveBuilder({ value, onChange, token }) {
  const c = theme.colors;
  const [uploading, setUploading] = useState(false);

  const set = (patch) => onChange({ ...value, ...patch });
  const setAction = (patch) => onChange({ ...value, action: { ...value.action, ...patch } });

  const inputStyle = { width: '100%', padding: '8px', background: c.background, color: c.text, border: `1px solid ${c.border}`, borderRadius: 6, boxSizing: 'border-box' };
  const label = { display: 'block', fontSize: 12, color: c.textMuted, margin: '10px 0 4px' };
  const counter = (len, max) => <span style={{ fontSize: 10, color: len >= max ? c.error : c.textMuted, float: 'right' }}>{len}/{max}</span>;
  const chk = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: c.text, cursor: 'pointer', margin: '12px 0 4px' };

  const isList = value.action?.type === 'list';

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch('/api/agent/archives/upload', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await r.json();
      if (data.url) set({ header: { ...value.header, url: data.url } });
    } catch { /* silent */ }
    setUploading(false);
  };

  const changeActionType = (type) => {
    if (type === 'quick_reply') onChange({ ...value, action: { type: 'quick_reply', buttons: [{ text: '' }] } });
    else if (type === 'url') onChange({ ...value, action: { type: 'url', text: '', url: '' } });
    // Las listas no admiten encabezado de media (limitación de Meta): lo quitamos.
    else onChange({ ...value, header: null, action: { type: 'list', buttonText: '', sectionTitle: '', rows: [{ title: '', description: '' }] } });
  };

  return (
    <div>
      {/* Encabezado (header media) — no disponible para listas */}
      {!isList && (
        <>
          <label style={chk}>
            <input type="checkbox" checked={!!value.header}
              onChange={(e) => set({ header: e.target.checked ? { type: 'image', url: '' } : null })} />
            Incluir encabezado (imagen / video / documento)
          </label>
          {value.header && (
            <div style={{ border: `1px solid ${c.border}`, borderRadius: 8, padding: 8, marginBottom: 4 }}>
              <select style={inputStyle} value={value.header.type}
                onChange={(e) => set({ header: { type: e.target.value, url: '' } })}>
                {HEADER_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
              <div style={{ marginTop: 8 }}>
                <input type="file" onChange={(e) => upload(e.target.files[0])} />
                {uploading && <div style={{ color: c.textMuted, fontSize: 12 }}>Subiendo…</div>}
                {value.header.url && <div style={{ color: c.success, fontSize: 12, wordBreak: 'break-all' }}>{value.header.url}</div>}
              </div>
            </div>
          )}
        </>
      )}

      {/* Cuerpo */}
      <label style={label}>Cuerpo del mensaje {counter((value.body || '').length, LIMITS.BODY)}</label>
      <textarea style={{ ...inputStyle, minHeight: 80 }} maxLength={LIMITS.BODY}
        placeholder="Escribe el mensaje…" value={value.body}
        onChange={(e) => set({ body: e.target.value })} />

      {/* Pie (footer) */}
      <label style={chk}>
        <input type="checkbox" checked={value.footer != null}
          onChange={(e) => set({ footer: e.target.checked ? '' : null })} />
        Incluir pie de mensaje (footer)
      </label>
      {value.footer != null && (
        <>
          {counter(value.footer.length, LIMITS.FOOTER)}
          <input style={inputStyle} maxLength={LIMITS.FOOTER} placeholder="Texto del pie"
            value={value.footer} onChange={(e) => set({ footer: e.target.value })} />
        </>
      )}

      {/* Tipo de interacción */}
      <label style={label}>Tipo de interacción</label>
      <select style={inputStyle} value={value.action?.type || 'quick_reply'} onChange={(e) => changeActionType(e.target.value)}>
        {ACTION_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
      </select>

      {/* Botones de respuesta rápida */}
      {value.action?.type === 'quick_reply' && (
        <div style={{ marginTop: 8 }}>
          {value.action.buttons.map((b, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <input style={inputStyle} maxLength={LIMITS.BUTTON_TITLE} placeholder={`Botón ${i + 1}`}
                value={b.text}
                onChange={(e) => {
                  const buttons = value.action.buttons.slice();
                  buttons[i] = { text: e.target.value };
                  setAction({ buttons });
                }} />
              {value.action.buttons.length > 1 && (
                <button onClick={() => setAction({ buttons: value.action.buttons.filter((_, j) => j !== i) })}
                  style={{ background: 'none', border: `1px solid ${c.error}`, color: c.error, borderRadius: 6, padding: '6px 9px', cursor: 'pointer' }}>✕</button>
              )}
            </div>
          ))}
          <button
            disabled={value.action.buttons.length >= LIMITS.BUTTONS_MAX}
            onClick={() => setAction({ buttons: [...value.action.buttons, { text: '' }] })}
            style={{ background: 'none', border: `1px dashed ${c.border}`, color: value.action.buttons.length >= LIMITS.BUTTONS_MAX ? c.textMuted : c.primary, borderRadius: 6, padding: '6px 10px', cursor: value.action.buttons.length >= LIMITS.BUTTONS_MAX ? 'not-allowed' : 'pointer', width: '100%' }}>
            + Agregar botón (máx {LIMITS.BUTTONS_MAX})
          </button>
        </div>
      )}

      {/* Botón de enlace URL */}
      {value.action?.type === 'url' && (
        <div style={{ marginTop: 8 }}>
          <label style={label}>Texto del botón {counter((value.action.text || '').length, LIMITS.BUTTON_TITLE)}</label>
          <input style={inputStyle} maxLength={LIMITS.BUTTON_TITLE} placeholder="Ej: Ver más"
            value={value.action.text} onChange={(e) => setAction({ text: e.target.value })} />
          <label style={label}>Enlace (URL)</label>
          <input style={inputStyle} placeholder="https://…" value={value.action.url}
            onChange={(e) => setAction({ url: e.target.value })} />
        </div>
      )}

      {/* Lista de opciones */}
      {value.action?.type === 'list' && (
        <div style={{ marginTop: 8 }}>
          <label style={label}>Texto del botón que abre la lista {counter((value.action.buttonText || '').length, LIMITS.LIST_BUTTON)}</label>
          <input style={inputStyle} maxLength={LIMITS.LIST_BUTTON} placeholder="Ej: Ver opciones"
            value={value.action.buttonText} onChange={(e) => setAction({ buttonText: e.target.value })} />
          <label style={label}>Título de la sección (opcional) {counter((value.action.sectionTitle || '').length, LIMITS.SECTION_TITLE)}</label>
          <input style={inputStyle} maxLength={LIMITS.SECTION_TITLE} placeholder="Ej: Nuestros servicios"
            value={value.action.sectionTitle} onChange={(e) => setAction({ sectionTitle: e.target.value })} />
          <label style={label}>Opciones</label>
          {value.action.rows.map((r, i) => (
            <div key={i} style={{ border: `1px solid ${c.border}`, borderRadius: 8, padding: 8, marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: c.textMuted }}>Opción {i + 1} {counter((r.title || '').length, LIMITS.ROW_TITLE)}</span>
                {value.action.rows.length > 1 && (
                  <button onClick={() => setAction({ rows: value.action.rows.filter((_, j) => j !== i) })}
                    style={{ background: 'none', border: 'none', color: c.error, cursor: 'pointer' }}>✕</button>
                )}
              </div>
              <input style={inputStyle} maxLength={LIMITS.ROW_TITLE} placeholder="Título de la opción"
                value={r.title}
                onChange={(e) => { const rows = value.action.rows.slice(); rows[i] = { ...rows[i], title: e.target.value }; setAction({ rows }); }} />
              <div style={{ marginTop: 6 }}>{counter((r.description || '').length, LIMITS.ROW_DESC)}</div>
              <input style={inputStyle} maxLength={LIMITS.ROW_DESC} placeholder="Descripción (opcional)"
                value={r.description || ''}
                onChange={(e) => { const rows = value.action.rows.slice(); rows[i] = { ...rows[i], description: e.target.value }; setAction({ rows }); }} />
            </div>
          ))}
          <button
            disabled={value.action.rows.length >= LIMITS.ROWS_MAX}
            onClick={() => setAction({ rows: [...value.action.rows, { title: '', description: '' }] })}
            style={{ background: 'none', border: `1px dashed ${c.border}`, color: value.action.rows.length >= LIMITS.ROWS_MAX ? c.textMuted : c.primary, borderRadius: 6, padding: '6px 10px', cursor: value.action.rows.length >= LIMITS.ROWS_MAX ? 'not-allowed' : 'pointer', width: '100%' }}>
            + Agregar opción (máx {LIMITS.ROWS_MAX})
          </button>
          <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6 }}>
            Nota: las listas no admiten encabezado con imagen/video/documento (limitación de Meta).
          </div>
        </div>
      )}
    </div>
  );
}
